import {
    Component,
    ContentChild,
    EventEmitter,
    Input,
    Output,
    TemplateRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { List as ImmutableList } from 'immutable';
import { List } from '../../constants-types/PiList.model';
import { PiControlValueAccessor } from './OkapiControlValueAccessor.interface';

@Component({
    selector: 'ok-pillbox-with-typeahead',
    template: `
        @if (showTypeahead()) {
            <ok-typeahead
                (onTextChange)="typeaheadOnTextChange.emit($event)"
                (onSelected)="typeaheadOnSelected($event)"
                [list]="typeaheadList"
                [placeholder]="typeaheadPlaceholder"
                [(ngModel)]="typeaheadText"
                [searchStyling]="searchStyling"
                [isDisabled]="isDisabled"
                [isLoadingPossibilities]="isLoadingPossibilities"
                [isColorPickerEnabled]="isColorPickerEnabled"
                [ngStyle]="{
                    'margin-bottom':
                        isColorPickerEnabled || isCreatingGroup ? '15px' : '0px'
                }"
            >
                <ng-template let-item="item">
                    <ng-template
                        [ngTemplateOutlet]="typeahead"
                        [ngTemplateOutletContext]="{
                            item: item
                        }"
                    >
                    </ng-template>
                </ng-template>
            </ok-typeahead>
        }
        <ok-pillbox
            [(ngModel)]="boundValue"
            [isValidFunc]="pillIsValidFunc"
            [editablePills]="editablePills"
            [removablePills]="removablePills"
            (onEdit)="onEdit.emit($event)"
            (onRemove)="onRemove.emit($event)"
            (onPillSelect)="onPillSelect.emit($event)"
            [isSelected]="isSelected"
            [isColorPickerEnabled]="isColorPickerEnabled"
            (onColorChange)="onColorChange.emit()"
            [isDisabled]="isDisabled"
        >
            <ng-template let-item="item">
                <ng-template
                    [ngTemplateOutlet]="pillbox"
                    [ngTemplateOutletContext]="{
                        item: item
                    }"
                >
                </ng-template>
            </ng-template>
        </ok-pillbox>
    `,
    // Following is required for ControlValueAccessor
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: OkPillboxWithTypeaheadComponent,
            multi: true
        }
    ],
    standalone: false
})
export class OkPillboxWithTypeaheadComponent implements PiControlValueAccessor {
    @ContentChild('typeahead', { static: false })
    typeahead: TemplateRef<any>;
    @ContentChild('pillbox', { static: false })
    pillbox: TemplateRef<any>;

    @Input()
    isColorPickerEnabled: boolean;
    @Input()
    isCreatingGroup: boolean;
    @Input()
    isSelected: (input: any) => boolean;
    @Input()
    searchStyling: boolean;
    // Top level options
    @Input()
    pillboxLimit: number = null;
    @Input()
    isDisabled = false;
    @Input()
    isLoadingPossibilities = false;
    @Input()
    disablePropagation = false;
    @Output()
    onColorChange = new EventEmitter<void>();

    showTypeahead(): boolean {
        if (!this.selectedItems) {
            return false;
        }
        return (
            this.pillboxLimit === null ||
            this.selectedItems.size < this.pillboxLimit
        );
    }

    // Typeahead stuff
    typeaheadText = '';
    @Input()
    typeaheadList: List<any> = ImmutableList();
    @Output()
    typeaheadOnTextChange: EventEmitter<string> = new EventEmitter<string>();
    @Input()
    typeaheadPlaceholder: string;

    @Output()
    itemSelected: EventEmitter<any> = new EventEmitter<any>();

    typeaheadOnSelected(item: any): void {
        if (this.disablePropagation) {
            this.onAdd.emit(item);
        } else {
            this.selectedItems = this.selectedItems.push(item);
            this.onAdd.emit(item);
            this.propagateChange(this.selectedItems);
        }
        this.typeaheadText = '';
        this.itemSelected.emit(item);
    }

    // Pillbox stuff
    @Input()
    pillIsValidFunc: (input: any) => boolean = (_input: any) => true;

    @Input()
    editablePills = false;
    @Input()
    removablePills = true;

    @Output()
    onAdd: EventEmitter<any> = new EventEmitter();
    @Output()
    onRemove: EventEmitter<any> = new EventEmitter();
    @Output()
    onEdit: EventEmitter<any> = new EventEmitter();
    @Output()
    onPillSelect: EventEmitter<any> = new EventEmitter();

    private selectedItems: List<any>;
    // Value needed by ControlValueAccessor - it is a no-op that is replaced by framework
    propagateChange: (changed: List<any>) => void = () => {
        /* No-op required by ControlValueAccessor interface - see registerOnChange */
    };

    get boundValue(): List<any> {
        return this.selectedItems;
    }
    set boundValue(val: List<any>) {
        this.selectedItems = val;
        this.propagateChange(this.selectedItems);
    }

    /* Meet ControlValueAccessor Interface */
    // Do NOT propagate changes up here
    writeValue(value: List<any>): void {
        this.selectedItems = value;
    }
    registerOnChange(fn: any): void {
        this.propagateChange = (changed: List<any>) => fn.call(this, changed);
    }
    registerOnTouched(): void {
        /* No callback function registered OnTouched */
    }
    /** ***************************************/
}
